/** @format */

import {Trans, useTranslation} from 'react-i18next';

import Alert from '@mui/material/Alert';
import {Box, LinearProgress, Typography} from '@mui/material';
import {format} from 'date-fns';

import AreaChartMultiData from 'components/charts/AreaChartMultiData';
import BoxPlate from 'components/plates/BoxPlate';
import Breadcrumbs from 'components/Breadcrumbs';
import SensorBarList from 'components/SensorGroupBar/SensorBarList';
import {PeriodSelector} from 'components/PeriodSelector';
import {ROUTES} from 'constants/ROUTES';
import {SensorCategoriesCriteriaType} from 'types';
import {useCompanyContext} from 'contexts/CompanyContext';
import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';
import {sensorName} from 'helpers/sensor_names';

import {useRouteSensorPage} from 'helpers/hooks/useRouteSensorPage';

import {criteriaClasses as classes, CriteriaRoot as Root} from './styles';

import criticalImg from '../images/sensor-critical.svg';
import regularImg from '../images/sensor-regular.svg';
import lowImg from '../images/sensor-low.svg';

import {CAN_SWIPE_LIST, PERIODS} from './const';
import categoriesData from '../../Project/data/categories.json';
import {CategoriesData} from '../../Project/components/Issues/model';

import {ReportButton} from '../ReportButton';
import {getSensorsStat} from './helpers/getSensorsStat';
import {getGraphDataByCriteria} from './helpers/getGraphDataByCriteria';
import {filterChecksByGroup} from './helpers/filterChecksByGroup';
import {mapSensorsToCategory} from './helpers/mapSensorsToCategory';
import {useGetCurrentUserQuery} from '../graphql/__generated__';
import {getSensorGroup} from './helpers/getSensorGroup';
import {sortChecks} from '../helpers/sortChecks';

interface Props {
  categoryName: SensorCategoriesCriteriaType;
}

const CategoryCriteria = ({categoryName}: Props) => {
  const {currentCompany} = useCompanyContext();

  const {t} = useTranslation();
  const {onSensorClick} = useRouteSensorPage();

  const getPageParams = () => {
    switch (categoryName) {
      case 'critical':
        return {
          title: t('Критичные сенсоры'),
          description: t(
            'Исправляйте в первую очередь — это самые болезненные для бюджета проблемы',
          ),
          icon: criticalImg,
        };
      case 'normal':
        return {
          title: t('Обычные сенсоры'),
          description: t(
            'Возвращайтесь к ним после критичных — срочности нет, но откладывать надолго не стоит',
          ),
          icon: regularImg,
        };
      case 'low':
        return {
          title: t('Некритичные сенсоры'),
          description: t(
            'Можно пропустить, если нет времени — на ключевые метрики почти не влияют',
          ),
          icon: lowImg,
        };
      default:
        return {
          title: '',
          description: '',
          icon: '',
        };
    }
  };

  const {title, description, icon} = getPageParams();

  const breadcrumbs = [
    {
      href: ROUTES.INDEX,
      title: <Trans>Проекты</Trans>,
    },
    {
      href: `/${ROUTES.HOME}`,
      title: <Trans>{currentCompany.name}</Trans>,
    },
    {
      href: '',
      title,
    },
  ];

  const {
    startDate,
    endDate,
    activePeriod,
    onChangePeriod,
    onChangeEndDate,
    onChangeStartDate,
    onSwipePeriod,
  } = usePeriodSelector({
    defaultPeriod: 'quarter',
  });

  const {data, loading, error} = useGetCurrentUserQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    },
  });

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser?.currentCompany) {
    return <LinearProgress style={{flex: 1}} data-testid='loading' />;
  }

  const handleOnDateRangeConfirm = (newStartDate: Date, newEndDate: Date) => {
    onChangeStartDate(newStartDate);
    onChangeEndDate(newEndDate);
  };

  const currentCompanyData = data.currentUser.currentCompany;
  const graphSensorChecks = data.currentUser.currentCompany.graphSensorChecks;
  const lastChecks = data.currentUser.currentCompany.lastChecks;
  const sensorGroups = data.currentUser.currentCompany.sensorGroups;

  const {sensors, foundNew, foundAtPeriodStart, leftAtPeriodEnd, foundFixed} = getSensorsStat({
    categoryName,
    currentCompany: currentCompanyData,
    startDate,
    endDate,
  });

  const areaChartData = getGraphDataByCriteria(sensors, graphSensorChecks);

  const typedCategoriesData = categoriesData as CategoriesData[];
  const sensorGroupsConverted = getSensorGroup(sensorGroups, categoryName);
  const sensorsList = sortChecks(filterChecksByGroup(lastChecks, sensorGroupsConverted), t);
  const sensorCategoryMap = mapSensorsToCategory(typedCategoriesData);

  return (
    <Root data-testid='category-criteria'>
      <Box
        sx={{
          mt: 12,
          mb: 10,
        }}
      >
        <Breadcrumbs items={breadcrumbs} />
      </Box>

      <Box className={classes.sensorsCountBox}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box className={classes.categoryIcon}>
            <img src={icon} alt='' />
          </Box>
          <Typography className={classes.categoryTitle}>{title}</Typography>
          <Typography className={classes.categorySubtitle} data-testid='sensors-count'>
            {t('plurals.sensors', {count: sensorsList.length})}
          </Typography>
        </Box>
        <ReportButton sensors={sensors} />
      </Box>

      <Box
        sx={{
          mt: 6,
        }}
      >
        <Typography className={classes.categoryDescription}>{description}</Typography>
      </Box>

      <Box className={classes.periodSelectorBox}>
        <PeriodSelector
          startDate={startDate}
          endDate={endDate}
          activePeriod={activePeriod}
          onChangePeriod={onChangePeriod}
          onDateRangeConfirm={handleOnDateRangeConfirm}
          periodsModel={PERIODS}
          canSwipe={CAN_SWIPE_LIST.includes(activePeriod)}
          onSwipePeriod={onSwipePeriod}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Было на начало периода</Trans>
          </Typography>
          <Typography className={classes.infoValue} data-testid='stat-start'>
            {foundAtPeriodStart}
          </Typography>
        </Box>
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Сработало</Trans>
          </Typography>
          <Typography className={classes.infoValue} data-testid='stat-new'>
            {foundNew}
          </Typography>
        </Box>
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Исправлено</Trans>
          </Typography>
          <Typography className={classes.infoValue} data-testid='stat-fixed'>
            {foundFixed}
          </Typography>
        </Box>
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Осталось</Trans>
          </Typography>
          <Typography className={classes.infoValue} data-testid='stat-left'>
            {leftAtPeriodEnd}
          </Typography>
        </Box>
      </Box>

      <Box sx={{margin: '8px 0 40px 0'}}>
        <BoxPlate className={classes.graphContainer}>
          <AreaChartMultiData data={areaChartData} />
        </BoxPlate>
      </Box>

      <Box>
        {sensorsList.map(check => (
          <SensorBarList
            key={check.sensor.id}
            name={check.sensor.name}
            description={sensorName(check.sensor.name)}
            totalFound={check.found}
            totalNew={check.new}
            title={sensorCategoryMap[check.sensor.name].category}
            icon={sensorCategoryMap[check.sensor.name].icon}
            onClickRow={onSensorClick}
          />
        ))}
      </Box>
    </Root>
  );
};

export {CategoryCriteria};
